import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { Pastilla } from '@/shared/ui/Pastilla/Pastilla'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'

import { ROL_EN_LA_APP } from './rolEnLaApp'
import estilos from './UsuariosDeOficina.module.css'

type PerfilDeOficina = {
  id: string
  nombre: string
  usuario: string | null
  rol: string
  activo: boolean
  /** Quién es en la empresa —«Gerente general · dueño»—, no lo que puede hacer. */
  cargo: string | null
}

/**
 * LOS QUE ENTRAN A LA APP SIN SER PERSONAL DE CAMPO.
 *
 * Johan: «¿por qué acá no me aparece el usuario de Camilo?». Camilo —gerencia—
 * tiene usuario, pero no es colaborador: no ejecuta órdenes, así que no está
 * en la nómina de campo, y la pantalla solo mostraba esa nómina. Quien quería
 * saber con qué entra alguien de la oficina no tenía dónde mirarlo.
 *
 * Aquí van los usuarios sin ficha de colaborador. Las cuentas de prueba y la
 * de ARKANO no: no son de MEGA.
 */
export function UsuariosDeOficina({ conFicha }: { conFicha: Set<string> }) {
  const { data = [], error } = useQuery({
    queryKey: ['perfiles', 'oficina'],
    queryFn: async (): Promise<PerfilDeOficina[]> => {
      const { data, error } = await supabase
        .from('perfiles')
        .select('id, nombre, usuario, rol, activo, cargo')
        .eq('activo', true)
        // Técnicos no: esos van en la nómina de arriba, y uno sin ficha
        // activa es una ficha dada de baja, no alguien de oficina.
        .in('rol', ['gerencia', 'gerente_operativo'])
        .order('nombre')

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as unknown as PerfilDeOficina[]
    },
  })

  // Gerencia primero y, dentro, quien tiene cargo (el gerente general) arriba.
  const orden = (p: PerfilDeOficina) => (p.rol === 'gerencia' ? 0 : 1) * 2 + (p.cargo ? 0 : 1)
  const deOficina = data
    .filter((p) => !conFicha.has(p.id))
    .sort((a, b) => orden(a) - orden(b) || a.nombre.localeCompare(b.nombre))
  if (error || deOficina.length === 0) return null

  return (
    <Tarjeta
      titulo="Gerencia y oficina"
      subtitulo="Entran a la aplicación pero no salen a campo, así que no tienen ficha de colaborador"
    >
      <ul className={estilos.lista}>
        {deOficina.map((p) => {
          const rol = ROL_EN_LA_APP[p.rol] ?? { etiqueta: p.rol, tono: 'neutro' as const }
          return (
            <li key={p.id} className={estilos.fila}>
              <span className={estilos.quien}>
                <span className={estilos.nombre}>{p.nombre}</span>
                {p.cargo && <span className={estilos.cargo}>{p.cargo}</span>}
              </span>
              <Pastilla tono={rol.tono}>{rol.etiqueta}</Pastilla>
              <span className={estilos.usuario}>
                {p.usuario ? (
                  <span className="codigo">{p.usuario}</span>
                ) : (
                  <span className="apagado">Entra con su correo</span>
                )}
              </span>
            </li>
          )
        })}
      </ul>
    </Tarjeta>
  )
}
